import type { IconName } from '@malash-office/ui-icons';
import { pageView } from '@malash-office/types';

/**
 * Not a page: the menu entry that signs out. It sits in the list so it is drawn like its neighbours;
 * the layouts catch this `to` before navigating (DashboardLayout, MobileNav).
 */
export const LOGOUT_PATH = '/logout';

export interface NavChild {
  label: string;
  icon: IconName;
  to: string;
  permission?: string;
}

export interface NavItem {
  label: string;
  icon: IconName;
  /** Direct link (leaf item). Mutually exclusive with `children`. */
  to?: string;
  permission?: string;
  /** When present, hovering the rail item opens a flyout with these sub-pages. */
  children?: NavChild[];
  /** Hide from the desktop right rail (still shown in the mobile nav). Reached via the profile. */
  hideInDesktopRail?: boolean;
}

/**
 * Right-rail navigation. Mirrors the reference dashboard: leaf items link directly,
 * parent items open a hover flyout with sub-pages. Items/children are filtered by permission.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    // Was a leaf pointing straight at `/planning`. It became a group once לקוחות ייצור joined —
    // and פיתוח מוצרים keeps the old address, so every link written to `/planning` before the
    // split still lands on the same page.
    //
    // המשימות שלי / צ׳אט / צוות are deliberately absent: they are reached from the buttons on
    // פיתוח מוצרים itself (`ProjectList.tsx`), and listing them here only made the flyout long.
    // Their routes are unchanged, so old links and the in-page navigation still work.
    label: 'תכנון ופיתוח',
    icon: 'monitor',
    children: [
      { label: 'פיתוח מוצרים', icon: 'monitor', to: '/planning', permission: pageView('planning-projects') },
      // ספריית קבצים is listed even though המשימות שלי / צ׳אט / צוות are not: those three are
      // reached from buttons on פיתוח מוצרים itself, and the library is the one screen somebody
      // opens *without* having a project in mind — which is exactly what a menu entry is for.
      { label: 'ספריית קבצים', icon: 'folder', to: '/planning/files', permission: pageView('planning-files') },
      // לקוחות ייצור is its own page: it is not the planning module's data, and a purchasing user
      // may hold one without the other.
      {
        label: 'לקוחות ייצור',
        icon: 'organization',
        to: '/planning/customers',
        permission: pageView('production-customers')
      },
      // ספקים — who makes the goods. Its own page, though the routes it calls are the planning module's.
      { label: 'ספקים', icon: 'contactsBook', to: '/planning/suppliers', permission: pageView('suppliers') }
    ]
  },
  {
    // Was a leaf pointing at `/production`, which never resolved to a screen — the planning module
    // left the slot empty on purpose (`docs/product-planning/PROGRESS.md`). הזמנות ספקים is its first
    // real child, so it became a group; שילוח ספקים follows the goods from the supplier to our shelf.
    label: 'יצור ורכש',
    icon: 'cart',
    children: [
      { label: 'הזמנות ספקים', icon: 'receipt', to: '/production/orders', permission: pageView('production-orders') },
      {
        label: 'שילוח ספקים',
        icon: 'location',
        to: '/production/shipments',
        permission: pageView('supplier-shipments')
      },
      // What the suppliers billed: the invoices, the suppliers behind them, and the money by month.
      { label: 'חשבוניות ספקים', icon: 'form', to: '/production/invoices', permission: pageView('supplier-invoices') }
    ]
  },
  {
    label: 'ניהול מוצרים',
    icon: 'tableHeart',
    children: [
      // Product Hub. Each of its screens joins this list in the PR that builds it — a nav link to
      // a route that does not exist yet lands on the catch-all redirect, which reads as the menu
      // being broken rather than as the feature being unfinished.
      { label: 'לוח בקרה', icon: 'monitor', to: '/products/overview', permission: pageView('products-overview') },
      { label: 'מוצרים', icon: 'folder', to: '/products/catalog', permission: pageView('products') },
      { label: 'חנויות', icon: 'organization', to: '/products/stores', permission: pageView('stores') },
      { label: 'מחירונים', icon: 'donation', to: '/products/pricelists', permission: pageView('pricelists') },
      // `cart` rather than one of the icons the plan listed: `calendar` and `tableHeart` would both
      // be misleading labels for a stock screen, and the icon set is fixed (`libs/ui-icons`).
      { label: 'מלאי', icon: 'cart', to: '/products/inventory', permission: pageView('inventory') },
      // Warehouses sat inside מלאי as a tab until it earned a menu entry: the list of locations is
      // its own question, and reaching it cost two clicks through a screen about stock levels.
      { label: 'מחסנים', icon: 'organization', to: '/products/warehouses', permission: pageView('warehouses') },
      { label: 'שילוח ואספקה', icon: 'location', to: '/products/fulfillment', permission: pageView('fulfillment') },
      { label: 'מבצעים', icon: 'ticket', to: '/products/promotions', permission: pageView('promotions') },
      { label: 'הצעות מחיר', icon: 'receipt', to: '/products/quotes', permission: pageView('quotes') },
      { label: 'אינטגרציות', icon: 'globe', to: '/products/integrations', permission: pageView('integrations') },
      {
        label: 'ניהול מנויים ותוכניות',
        icon: 'ticket',
        to: '/products/subscriptions',
        permission: pageView('subscriptions')
      },
      { label: 'דפי נחיתה', icon: 'globe', to: '/products/landing-pages', permission: pageView('landing-pages') }
    ]
  },
  {
    label: 'מחסן והזמנות',
    icon: 'receipt',
    children: [
      // תצוגת מחסן (/warehouse/subscription-orders/packing) is deliberately not listed here:
      // it is reached from the button on the orders page, not from the menu.
      //
      // הזמנות is first: it is the module's main work queue — every order from every connected
      // store — while הזמנות מנויים is the shipments we generate ourselves.
      { label: 'הזמנות', icon: 'receipt', to: '/warehouse/orders', permission: pageView('orders') },
      // ליקוט הזמנות is the warehouse's own page, deliberately apart from הזמנות — a picker is
      // granted this one alone and sees this entry without the one above.
      { label: 'ליקוט הזמנות', icon: 'cart', to: '/warehouse/picking', permission: pageView('picking') },
      {
        label: 'הזמנות מנויים',
        icon: 'cart',
        to: '/warehouse/subscription-orders',
        permission: pageView('subscription-orders')
      }
    ]
  },
  { label: 'לוגיסטיקה ומשלוחים', icon: 'location', to: '/logistics' },
  {
    // The mockup's group (Claude Design, "שירות לקוחות"): the inbox first; broadcasts and the service
    // dashboard join it as they are built (docs/inbox/PLAN.md §9). The old "שירות לקוחות" group and its
    // order mailing were folded in (2026-10-29) — one menu for talking to customers, not two.
    label: 'תקשורת ולקוחות',
    icon: 'chat',
    children: [
      { label: 'שירות לקוחות', icon: 'chat', to: '/communication/inbox', permission: pageView('inbox') },
      { label: 'דיוורי WhatsApp', icon: 'sent', to: '/communication/broadcasts', permission: pageView('broadcasts') },
      {
        label: 'תבניות WhatsApp',
        icon: 'form',
        to: '/communication/templates',
        permission: pageView('whatsapp-templates')
      },
      {
        label: 'דיוור הזמנות',
        icon: 'globe',
        to: '/customer-service/order-mailing',
        permission: pageView('order-mailing')
      }
    ]
  },
  {
    label: 'הנהלת חשבונות וכספים',
    icon: 'donation',
    children: [
      { label: 'ניהול גביה', icon: 'donation', to: '/finance/collection', permission: pageView('collection') },
      { label: 'תשלומים', icon: 'ticket', to: '/finance/payments', permission: pageView('payments') },
      { label: 'לקוחות', icon: 'contacts', to: '/finance/customers', permission: pageView('customers') }
    ]
  },
  { label: 'דוברות והפצה', icon: 'globe', to: '/pr' },
  {
    // ניהול (2026-10-21): the user's own corner — the personal area, which used to be a hidden leaf
    // reached from the profile, and פניות ומשוב. Every staff member sees both: filing a request
    // needs no page (`STAFF_BASELINE_KEYS`); the manager's side is inside the screen.
    // התנתקות moved here (2026-10-25) from the button above the user's name in the rail.
    // Renamed הגדרות (2026-10-29), as asked.
    label: 'הגדרות',
    icon: 'desk',
    children: [
      { label: 'אזור אישי', icon: 'desk', to: '/' },
      { label: 'פניות ומשוב', icon: 'chat', to: '/management/feedback', permission: 'feedback:submit' },
      // הוצאות אשראי (2026-10-26): every staff member asks to pay by card here; the office's side is inside.
      {
        label: 'בקשת תשלום באשראי',
        icon: 'receipt',
        to: '/management/credit-expenses',
        permission: 'credit-expenses:submit'
      },
      { label: 'התנתקות', icon: 'powerButton', to: LOGOUT_PATH }
    ]
  },
  {
    label: 'ניהול מערכת',
    icon: 'settings',
    children: [
      { label: 'משתמשים', icon: 'people', to: '/users', permission: pageView('users') },
      // The dashboard's users (sm-dashboard), shown and edited through our backend — not our staff.
      { label: 'משתמשי דשבורד', icon: 'contactsBook', to: '/dashboard-users', permission: pageView('dashboard-users') },
      { label: 'תפקידים והרשאות', icon: 'identityCard', to: '/roles', permission: pageView('roles') },
      // Every mail connection in one screen: employees' personal mailboxes, אגפים (Smoove) and
      // customer-service mailboxes. docs/product-planning/MAIL-CONNECTIONS.md.
      { label: 'חיבור מיילים', icon: 'contacts', to: '/mail-connections', permission: pageView('mail-connections') },
      // Push to employees' phones — manual sends, history, customer-request recipients (PWA-PUSH.md).
      { label: 'התראות', icon: 'sent', to: '/notifications', permission: pageView('push-notifications') }
    ]
  }
];
